import React, { useEffect, useState, useRef } from 'react'
import { useDispatch, useSelector } from 'react-redux';
import { LoadingWithOveraly } from '../wrapper/loader';
import actions from './../../actions/action.conversations';
import ConversationTable from './../conversation/ConversationTable';
import ModalDialog from "../../components/modal-dialog/modalDialog";
import { Form, FormGroup, Label, Input, Alert } from "reactstrap";
import { unstable_batchedUpdates } from 'react-dom';
import "./scss/messages.scss";
import { changeToTitleCase } from '../../utils/convertionUtils';
import SearchBar from '../searchBar/SearchBar';
import { CircleXIcon, AlertErrorCircleIcon } from 'assets/icons/components'
import { MESSAGE_PLACEHOLDERS, MESSAGE_ERRORS, MESSAGE_TABS, MESSAGE_STATUSES, MESSAGE_CRM_CASE_ID_API_FAILED } from '../../constants/constant.message';

const MessagesComponent = ({ memberId }) => {

    const [isModalOpen, setIsModalOpen] = useState(false);

    const [currentConversation, setCurrentConversation] = useState(null);

    // Form field states
    const [isNewMessageForm, setIsNewMessageForm] = useState(false);
    const [subject, setSubject] = useState('');
    const [message, setMessage] = useState('');
    const [files, setFiles] = useState([]);
    const [crmCaseId, setCrmCaseId] = useState('');

    // Form behaviour states
    const [isSubjectFocused, setIsSubjectFocused] = useState(false);
    const [isCrmCaseIdFocused, setIsCrmCaseIdFocused] = useState(false);
    const [isMessageFocused, setIsMessageFocused] = useState(false);
    const [characterRequired, setCharacterRequired] = useState(false);
    const [filesUploadErrorDisplay, setFilesUploadErrorDisplay] = useState(false);
    const [filesDisplay, setFilesDisplay] = useState(false);

    // message filtering and searching
    const [currentSearchCrmCaseId, setCurrentSearchCrmCaseId] = useState("");
    const [searchedCrmCaseId, setSearchedCrmCaseId] = useState("");
    const [showUnrepliedMessagesFilter, setShowUnrepliedMessagesFilter] = useState(false);

    const memberProfile = useSelector(state => state.memberProfile) || {};
    const dependentProfile = useSelector(state => state.memberFamilyProfile) || {};
    const { conversations, filteredConversationsByCrmCaseId, loading, sendingMessage, sendingMessageError, replyFormValidation, changingConversationStatus } = useSelector(state => state.conversations);
    const { isCaseNumberNotCreated, isCaseNumberNotInCurrentConversation, areCrmApisOffline } = replyFormValidation;
    const isCaseNumberNotCreatedRef = useRef(false);
    const previousAreCrmApisOfflineRef = useRef(false);
    const areCrmApisOfflineRef = useRef(false);

    const [messageTab, setMessageTab] = useState(MESSAGE_TABS.INBOX);
    const dispatch = useDispatch();

    const profile = dependentProfile?.memberDetail ? dependentProfile : memberProfile;
    const profileDetail = dependentProfile?.memberDetail ? dependentProfile.memberDetail : memberProfile.memberDetail;
    const fullName = currentConversation?.member ? changeToTitleCase(currentConversation?.member?.firstName + ' ' + currentConversation?.member?.lastName) : profileDetail?.name;
    const showCaseNumberNotInThreadWarning = isCaseNumberNotInCurrentConversation && !isNewMessageForm && !isCaseNumberNotCreated;
    const conversationStatus = messageTab === MESSAGE_TABS.INBOX || messageTab === MESSAGE_TABS.INBOX_API_FAILED ? MESSAGE_STATUSES.CSR_ACTION_REQUIRED : MESSAGE_STATUSES.COMPLETE;

    useEffect(() => {
      isCaseNumberNotCreatedRef.current = isCaseNumberNotCreated
    }, [isCaseNumberNotCreated])

    useEffect(() => {
      areCrmApisOfflineRef.current = areCrmApisOffline
    }, [areCrmApisOffline])

    useEffect(() => {
      setCharacterRequired(false)
      dispatch(actions.resetErrorStates())

      if (crmCaseId.length > 4) {
        dispatch(actions.validateReplyFormCrmCaseIdAction(crmCaseId, false))
      } else {
        dispatch(actions.resetReplyFormValidationStatuses())
      }

      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [subject, message, files, crmCaseId])

    useEffect(() => {
      setCurrentSearchCrmCaseId('')
      setSearchedCrmCaseId('')
      setShowUnrepliedMessagesFilter(false)
    }, [messageTab])

    const restoreFormState = () => {
        setFilesUploadErrorDisplay(false)
        setCharacterRequired(false)

        setIsNewMessageForm(false)
        setSubject('')
        setMessage('')
        setFiles([])
        setCrmCaseId('')
        setFilesDisplay(false)
        dispatch(actions.resetReplyFormValidationStatuses())
        dispatch(actions.resetErrorStates())
    }

    //close the modal once the message is
    useEffect(() => {
        if (!sendingMessage && !sendingMessageError) {

            unstable_batchedUpdates(() => {
                setIsModalOpen(false)
                restoreFormState()
            });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [sendingMessage]);

    useEffect(() => {
      if (!searchedCrmCaseId) {
        if (messageTab === MESSAGE_TABS.INBOX_API_FAILED) {
          if (memberId && profileDetail) {
            dispatch(actions.searchMessagesByCrmCaseIdAction(MESSAGE_CRM_CASE_ID_API_FAILED, conversationStatus, profileDetail.memberUuid, showUnrepliedMessagesFilter))
          } else {
            dispatch(actions.searchMessagesByCrmCaseIdAction(MESSAGE_CRM_CASE_ID_API_FAILED, conversationStatus, null, showUnrepliedMessagesFilter))
          }
        } else if (memberId && profileDetail) {
          dispatch(actions.getConversationsByRecipientIdAction(profileDetail.memberUuid, null, messageTab, showUnrepliedMessagesFilter))
        } else {
          dispatch(actions.getConversationsByStatusAction(conversationStatus, null, messageTab, showUnrepliedMessagesFilter))
        }
      }

      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [messageTab, searchedCrmCaseId, profileDetail, sendingMessage, changingConversationStatus, showUnrepliedMessagesFilter])

    useEffect(() => {
      if (searchedCrmCaseId) {
        const isInboxApiFailedTab = messageTab === MESSAGE_TABS.INBOX_API_FAILED

        if (memberId && profileDetail) {
          dispatch(actions.searchMessagesByCrmCaseIdAction(searchedCrmCaseId, conversationStatus, profileDetail.memberUuid, showUnrepliedMessagesFilter, isInboxApiFailedTab))
        } else {
          dispatch(actions.searchMessagesByCrmCaseIdAction(searchedCrmCaseId, conversationStatus, null, showUnrepliedMessagesFilter, isInboxApiFailedTab))
        }
      }

      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [searchedCrmCaseId, profileDetail, sendingMessage, changingConversationStatus, showUnrepliedMessagesFilter])

    const openModal = (conversation) => {

        if (conversation) {
            unstable_batchedUpdates(() => {
                setCurrentConversation(conversation);
                setSubject(conversation.topic);
                setIsModalOpen(!isModalOpen);
            })
        } else {

            unstable_batchedUpdates(() => {
                setCurrentConversation('');
                setSubject('');
                setIsModalOpen(!isModalOpen);
            })
        }

    }

    const displayAttachmentsIconHandler = (children) => {
        unstable_batchedUpdates(() => {
            setFilesDisplay(children.component);
            setFiles(children.files);
        })
    }

    const sendMessageHandler = async () => {
        if (message.length < 20) {
            setCharacterRequired('Minimum 20 character are required.')
        }
        else if (!subject) {
            setCharacterRequired('Subject is missing')
        } else {
            previousAreCrmApisOfflineRef.current = areCrmApisOfflineRef.current;
            await dispatch(actions.validateReplyFormCrmCaseIdAction(crmCaseId, true));

            const isCaseNumberCreated = !isCaseNumberNotCreatedRef.current
            const noApisOffline = !areCrmApisOfflineRef.current;
            const hasBeenWarnedAboutApisOffline = areCrmApisOfflineRef.current && previousAreCrmApisOfflineRef.current

            if ((isCaseNumberCreated && noApisOffline)|| hasBeenWarnedAboutApisOffline) {
              if (currentConversation) {
                dispatch(
                  actions.replyMessageByConversationIdAction(
                    {
                      recipientId: currentConversation.recipientId,
                      conversationId: currentConversation.conversationId,
                      message,
                      files,
                      crmCaseId
                    },
                    messageTab,
                    memberId
                  )
                )
              } else {
                dispatch(
                  actions.createConversationAction({
                    recipientId: profile.memberDetail.memberUuid,
                    topic: subject,
                    message,
                    files,
                    crmCaseId
                  })
                )
              }

              setFiles([])
              return 'success'
            }
        }

    }

    const displayAttachmentsError = (children) => {
        setFilesUploadErrorDisplay(children)
    };

    const onModalToggle = () => {
        setIsModalOpen(false);
        restoreFormState();
    }

    const pickSendReplyWarningText = () => {
        if(areCrmApisOffline){
            return MESSAGE_ERRORS.SEND_REPLY_CRM_APIS_OFFLINE;
        }

        if(showCaseNumberNotInThreadWarning){
            return MESSAGE_ERRORS.SEND_REPLY_NOT_IN_THREAD;
        }

        return '';
    }

    const pickCrmCaseIdInputMessage = () => {
        if(isCaseNumberNotCreated){
            return MESSAGE_ERRORS.CRM_CASE_ID_NOT_EXIST;
        }

        return MESSAGE_PLACEHOLDERS.CRM_CASE_ID_INPUT;
    }

    if (memberId && !profile.memberDetail) {
        return <LoadingWithOveraly/>
    }
    return (
        <div className='messages'>
            {loading && <LoadingWithOveraly/>}
            <ModalDialog
                className={"messages-modal"}
                title="In-App Message"
                showModal={isModalOpen}
                onToggle={onModalToggle}
                footerBtns={[
                    {
                        key: "send",
                        text: "Send",
                        onclick: sendMessageHandler,
                        className: "custom-round-btn btn btn-info",
                        icon: "icon icon-send",
                        disabled: crmCaseId.length < 5 || isCaseNumberNotCreated,
                    },
                ]}
                footerBtnsAttachement={true}
                displayAttachmentsIcon={displayAttachmentsIconHandler}
                displayAttachmentsError={displayAttachmentsError}
                loading={sendingMessage && !loading}
                showWarning={showCaseNumberNotInThreadWarning || areCrmApisOffline}
                warningHeadline={pickSendReplyWarningText()}
            >
                <Form>
                    {characterRequired && <Alert color="danger">{characterRequired}</Alert>}
                    {sendingMessageError && <Alert color="danger">{sendingMessageError}</Alert>}
                    <FormGroup>
                        <Label for="exampleEmail"><strong>To</strong></Label>
                        <Input
                            type="email"
                            name="emailId"
                            id="emailId"
                            value={fullName}
                            readOnly
                        />
                    </FormGroup>
                    <FormGroup>
                        <Label for="subject"><strong>Subject</strong></Label>
                        <div className={`${isSubjectFocused && !currentConversation ? 'input-focused' : ''}`}>
                            <Input
                                type="text"
                                name="subject"
                                id="subject"
                                placeholder="Subject"
                                value={subject}
                                readOnly={currentConversation}
                                onChange={(e) => {
                                    setSubject(e.target.value);
                                }}
                                onFocus={() => setIsSubjectFocused(true)}
                                onBlur={() => setIsSubjectFocused(false)}
                            />
                        </div>
                    </FormGroup>
                    <FormGroup>
                        <Label for="crmCaseId"><strong>CRM case no.</strong></Label>
                        <div className={`input-with-icon-wrapper ${isCaseNumberNotCreated ? 'input-error': isCrmCaseIdFocused ? 'input-focused' : ''}`}>
                            <Input
                                type="text"
                                name="crmCaseId"
                                id="crmCaseId"
                                placeholder="CRM case no."
                                value={crmCaseId}
                                onChange={(e) => {
                                    // Only allow numbers and limit to 10 characters
                                    const value = e.target.value;
                                    if (value === '' || /^\d+$/.test(value)) {
                                        if (value.length <= 10) {
                                            setCrmCaseId(value);
                                        }
                                    }
                                }}
                                onFocus={() => setIsCrmCaseIdFocused(true)}
                                onBlur={() => setIsCrmCaseIdFocused(false)}
                            />
                            {crmCaseId && (
                                <div className="clear-input-icon" onClick={() => setCrmCaseId('')}>
                                    <CircleXIcon width={20} height={20} />
                                </div>
                            )}
                        </div>
                        <div className="reply-to-thread-warning">
                            <div className={isCaseNumberNotCreated ? "crm-case-id-error" : ""}>
                                {isCaseNumberNotCreated && <AlertErrorCircleIcon width={15} height={15} className="alert-error-circle-icon" />}
                                {pickCrmCaseIdInputMessage()}
                            </div>
                        </div>
                    </FormGroup>
                    
                    
                    {filesUploadErrorDisplay && filesUploadErrorDisplay}
                    {filesDisplay && filesDisplay}

                    <FormGroup>
                        <Label for="message"><strong>Message</strong></Label>
                        <div className={`${isMessageFocused ? 'input-focused' : ''}`}>
                            <Input
                                className='message-text-area'
                                type="textarea"
                                name="message"
                                id="message"
                                placeholder="Message"
                                value={message}
                                onChange={(e) => {
                                    setCharacterRequired(false)
                                    setMessage(e.target.value);
                                }}
                                onFocus={() => setIsMessageFocused(true)}
                                onBlur={() => setIsMessageFocused(false)}
                            />
                        </div>
                        <div className="isDragActiveOverlay">
                            <div className="title">Drop files here</div>
                            <div className="subtitle">File types: pdf, doc, docx, jpg, png</div>
                        </div>
                    </FormGroup>
                </Form>
            </ModalDialog>
            {memberId ? (
                <div className="message-interfaces-container">
                <div className="left-container">
                    <button
                    type="button"
                    color="info"
                    className="custom-round-btn btn btn-info"
                    onClick={() => {
                        setIsNewMessageForm(true);
                        openModal();
                    }}
                    >
                    <i className="icon icon-mail" />
                    New Message
                    </button>
                </div>
                <div className="unreplied-filter-container">
                    <div className="unreplied-filter">
                        <Input
                            className='unreplied-filter-input'
                            type="checkbox"
                            id="unrepliedMessagesFilter"
                            checked={showUnrepliedMessagesFilter}
                            onChange={() => setShowUnrepliedMessagesFilter(!showUnrepliedMessagesFilter)}
                        />
                        <Label for="unrepliedMessagesFilter" className="unreplied-filter-label">
                            Show unreplied messages
                        </Label>
                    </div>
                </div>
                <div className="right-container">
                    <SearchBar currentSearchCrmCaseId={currentSearchCrmCaseId} setCurrentSearchCrmCaseId={setCurrentSearchCrmCaseId} submitSearch={setSearchedCrmCaseId} placeholderMessage={MESSAGE_PLACEHOLDERS.SEARCH_BAR}/>
                </div>
                </div>
            ) : (
                <div className='crm-search-bar-wrapper'>
                    <div className="unreplied-filter-wrapper">
                        <div className="unreplied-filter">
                            <Input
                                className='unreplied-filter-input'
                                type="checkbox"
                                id="unrepliedMessagesFilter"
                                checked={showUnrepliedMessagesFilter}
                                onChange={() => setShowUnrepliedMessagesFilter(!showUnrepliedMessagesFilter)}
                            />
                            <Label for="unrepliedMessagesFilter" className="unreplied-filter-label">
                                Show unreplied messages
                            </Label>
                        </div>
                    </div>
                    <div className='crm-search-bar-position'>
                        <SearchBar currentSearchCrmCaseId={currentSearchCrmCaseId} setCurrentSearchCrmCaseId={setCurrentSearchCrmCaseId} submitSearch={setSearchedCrmCaseId} placeholderMessage={MESSAGE_PLACEHOLDERS.SEARCH_BAR}/>
                    </div>
                </div>
            )}

            <ConversationTable
                conversations={searchedCrmCaseId || messageTab === MESSAGE_TABS.INBOX_API_FAILED ? filteredConversationsByCrmCaseId : conversations}
                member={memberId ? { ...profile.profile, ...profile.memberDetail } : {}}
                openReplyModal={openModal}
                messageTab={messageTab}
                setMessageTab={setMessageTab}
                searchedCrmCaseId={searchedCrmCaseId}
                showUnrepliedMessagesFilter={showUnrepliedMessagesFilter}
            />

        </div>
    )
}

export default MessagesComponent
